import { LANGUAGE_DISPLAY_NAMES } from '@shared/core/localized-defaults'
import classNames from 'classnames'
import { Info } from 'lucide-react'
import { useIntl } from 'react-intl'
import { Button, FormattedText, SelectField, Typography } from 'src/modules/core/components'
import { type CountryOption, formatCountryName } from 'src/modules/core/utils/country.utils'
import { COUNTRY_ANCHOR_ID } from './account-country-card.domain'
import type {
  AccountCountryCardProps,
  CountryDetailsProps,
  CountryFooterProps,
  CountrySelectProps,
} from './account-country-card.interface'
import classes from './account-country-card.module.scss'
import { useAccountCountryCard } from './use-account-country-card'

// SelectField drops the click that lands right after onMenuClose, and react-select also calls it on blur,
// so the first click on Save changes or Cancel after a pick was lost. Overriding it opts out of that.
function keepNextClick() {
  return undefined
}

function CountrySelect({ options, value, onChange, disabled }: CountrySelectProps) {
  const { formatMessage } = useIntl()
  return (
    <SelectField
      variant="small"
      className={classes.select}
      classNamePrefix="account-country"
      aria-label={formatMessage({ id: 'settings.country.title' })}
      placeholder={formatMessage({ id: 'settings.country.placeholder' })}
      options={options}
      value={value}
      isDisabled={disabled}
      isSearchable={true}
      closeMenuOnSelect={true}
      onMenuClose={keepNextClick}
      onChange={(option: CountryOption | null) => option && onChange(option.value)}
    />
  )
}

function CountryDetails({ saved, selected, isStaged, languageSwitch }: CountryDetailsProps) {
  return (
    <div className={classes.details} data-testid="account-country-details">
      {isStaged && (
        <Typography variant="body" className={classes.status}>
          {saved ? (
            <FormattedText id="settings.country.currently" values={{ value: formatCountryName(saved.value) }} />
          ) : (
            <FormattedText id="settings.country.currentlyNone" />
          )}
        </Typography>
      )}
      <Typography variant="body">
        {selected ? (
          <FormattedText id="settings.country.phoneHint" values={{ country: selected.label }} />
        ) : (
          <FormattedText id="settings.messageLanguage.noCountry" />
        )}
      </Typography>
      <Typography variant="body" className={classes.status}>
        <FormattedText id="settings.country.languageHint" />
      </Typography>
      {languageSwitch && (
        <div className={classes.warning} role="note">
          <Info className={classes.warningIcon} size={20} aria-hidden="true" />
          <Typography variant="body" className={classes.warningText}>
            <FormattedText
              id="settings.country.languageSwitch"
              values={{ language: LANGUAGE_DISPLAY_NAMES[languageSwitch].english }}
            />
          </Typography>
        </div>
      )}
    </div>
  )
}

function CountryFooter({ isSaving, onCancel, onSave }: CountryFooterProps) {
  return (
    <div className={classes.footer}>
      <Button type="button" variant="withBorder" widthType="content" onClick={onCancel} disabled={isSaving}>
        <FormattedText id="settings.country.cancel" />
      </Button>
      <Button type="button" widthType="content" onClick={onSave} isLoading={isSaving} disabled={isSaving}>
        <FormattedText id="settings.country.save" />
      </Button>
    </div>
  )
}

export function AccountCountryCard({ className }: AccountCountryCardProps) {
  const { formatMessage } = useIntl()
  const card = useAccountCountryCard()
  if (!card.profile) return null

  return (
    <section
      id={COUNTRY_ANCHOR_ID}
      className={classNames(classes.card, className)}
      aria-label={formatMessage({ id: 'settings.country.title' })}
      data-testid="account-country-card"
    >
      <Typography variant="h5" className={classes.title}>
        <FormattedText id="settings.country.title" />
      </Typography>
      <CountrySelect options={card.options} value={card.selected} onChange={card.stage} disabled={card.isSaving} />
      <CountryDetails
        saved={card.saved}
        selected={card.selected}
        isStaged={card.isStaged}
        languageSwitch={card.languageSwitch}
      />
      {card.isStaged && (
        <CountryFooter isSaving={card.isSaving} onCancel={card.cancel} onSave={() => void card.save()} />
      )}
    </section>
  )
}
